import React from 'react';

import { withAutoFocus } from '../hocs/withAutoFocus';
import { withControl } from '../hocs/withControl';
import { withControl as withControlDesktop } from '../hocs/withControl@desktop';
import { withDebounceInput } from '../hocs/withDebounceInput';
import { useTextFieldHint } from '../hooks/useTextFieldHint';
import { compose, composeU } from '../lib/compose';
import { useRenderOverride } from '../lib/render-override';
import { withAutoResize } from './_autoResize/Textarea_autoResize';
import { withHasClear } from './_hasClear/Textarea_hasClear';
import { withSizeL } from './_size/Textarea_size_l';
import { withSizeS } from './_size/Textarea_size_s';
import { withViewContrast } from './_view/Textarea_view_contrast';
import { TextareaBox as Box } from './Box/Textarea-Box';
import { TextareaControl as ControlOriginal } from './Control/Textarea-Control';
import { TextareaHint as Hint } from './Hint/Textarea-Hint';
import { cnTextarea } from './Textarea.cn';
import type { TextareaBaseProps } from './Textarea.types';
import { TextareaWrap as Wrap } from './Wrap/Textarea-Wrap';

import './Textarea.css';

const enhance = compose(
    withControl,
    withControlDesktop,
    withAutoFocus,
    withDebounceInput,
    composeU(withViewContrast),
    composeU(withSizeL, withSizeS),
    withHasClear,
);

/**
 * @deprecated Use import { HrTextarea } from '@yandex-int/hr-components/HrTextarea'
 */
export const Textarea = enhance(
    ({
        addonAfter,
        addonBefore,
        disabled,
        focused,
        hovered,
        onMouseEnter,
        onMouseLeave,
        innerRef,
        wrapRef,
        className: textareaClassName,
        // Извлекаем свойства, т.к. они не нужны на DOM узле
        // FIXME: https://github.com/bem/bem-react/issues/381
        pressed: _pressed,
        view = 'outline',
        size = 'm',
        hint: htmlHint,
        state,
        title,
        renderControl,
        ...props
    }: TextareaBaseProps) => {
        React.useEffect(() => {
            if (process.env.NODE_ENV === 'development')
                console.warn('[Textarea]: Эта версия компонента устарела и скоро будет удалена');
        }, []);

        const className = cnTextarea(
            {
                hovered,
                focused,
                disabled,
                state,
                size,
                view,
            },
            [textareaClassName],
        );

        const Control = useRenderOverride(ControlOriginal, renderControl);

        const { hint, hintProps } = useTextFieldHint({ hint: htmlHint });

        return (
            <span
                ref={innerRef}
                className={className}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                title={title}
            >
                {addonBefore}
                <Wrap innerRef={wrapRef}>
                    <Control {...props} aria-invalid={state === 'error'} disabled={disabled} />
                    <Box />
                </Wrap>
                {addonAfter}
                {hint && <Hint {...hintProps}>{hint}</Hint>}
            </span>
        );
    },
);

Textarea.displayName = cnTextarea();

export type TextareaProps = React.ComponentProps<typeof Textarea>;

export const TextareaWithAutoResize = compose(withAutoResize)(Textarea);
export type TextareaWithAutoResizeProps = React.ComponentProps<typeof TextareaWithAutoResize>;
